import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MerchantData } from '../merchants.interface';
import { OptionItem } from '../../../shared/types/api-responses.type';
import { MasterMerchantFormValidations } from './merchant-form-validations';
import { MerchantsService } from '../../../services/merchants/merchants.service';
import { firstValueFrom } from 'rxjs';
import { showAlert } from '../../../shared/helpers/alert';
import { FlatpickrOptions } from 'ng2-flatpickr';
import { MasterDepositFormValidations } from '../../deposits/deposit-form/deposit-form-validations';

@Component({
  selector: 'app-merchant-form',
  templateUrl: './merchant-form.component.html',
  styleUrls: ['./merchant-form.component.css'],
})
export class MerchantFormComponent implements OnInit {
  loading: boolean = false;
  form!: FormGroup;
  readonly id: string;
  merchantData!: MerchantData;
  paymentTypeOption: OptionItem[] = [
    { label: 'Prepaid', value: '0' },
    { label: 'Postpaid', value: '1' },
  ];

  readonly MasterMerchantFormValidations = MasterMerchantFormValidations;
  expiredDateConfig!: FlatpickrOptions;
  @ViewChild('expiredDatePicker') expiredDatePicker: any;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly merchantService: MerchantsService,
    private readonly router: Router
  ) {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
  }

  async ngOnInit(): Promise<void> {
    this.initForm();
    await this.loadInitialData();
  }

  private initForm(): void {
    this.form = this.fb.group({
      id: [''],
      code: [''],
      name: ['', Validators.required],
      user: [''],
      password: [''],
      secret_code: [''],
      callback_url: [''],
      expired_date: [null], // Initialize it as a Date object
      payment_type: [''],
      balance: [0],
      bank_account: [''],
      bank_name: [''],
      bank_branch: [''],
      add_params1: [''],
      add_params2: [''],
    });

    this.form.get('payment_type')?.setValue('0');

    this.form.valueChanges.subscribe((value) => console.log('Form Value Changed:', value));
  }

  ngAfterViewInit(): void {
    if (this.expiredDatePicker) {
      this.expiredDatePicker.flatpickr.setDate(new Date());
    }
  }

  private async loadInitialData(): Promise<void> {
    this.loading = true;
    try {
      if (this.id) {
        await this.getMerchantData();
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      this.showNotification('error', 'Error', 'Failed to load some initial data.');
    } finally {
      this.loading = false;
    }
  }

  private async getMerchantData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.merchantService.getMerchantById(this.id));
      this.merchantData = data;
      this.patchMerchantData(data);
    } catch (error) {
      this.showNotification('error', 'Failed to fetch merchant', JSON.stringify(error));
    }
  }

  private patchMerchantData(merchant: MerchantData): void {
    console.log('Patching Merchant Data:', merchant);
    console.log('Form Controls Before Patch:', this.form.controls);

    const formattedDate = merchant.expired_date
      ? new Date(merchant.expired_date).toLocaleDateString('en-GB') // Formats to dd/mm/yyyy
      : undefined;
    console.log('Formatted Expired Date:', formattedDate);

    this.form.patchValue({
      id: merchant.id,
      code: merchant.code,
      name: merchant.name,
      user: merchant.user,
      password: merchant.password,
      secret_code: merchant.secret_code,
      callback_url: merchant.callback_url,
      expired_date: formattedDate,
      payment_type: merchant.payment_type,
      balance: merchant.balance,
      bank_account: merchant.bank_account,
      bank_name: merchant.bank_name,
      bank_branch: merchant.bank_branch,
      add_params1: merchant.add_params1,
      add_params2: merchant.add_params2,
    });

    console.log('Form Value After Patch:', this.form.value);

    if (this.expiredDatePicker?.flatpickr) {
      this.expiredDatePicker.flatpickr.setDate(formattedDate, true);
    }

    this.expiredDateConfig = {
      defaultDate: formattedDate,
      dateFormat: 'd/m/Y',
      altInput: true,
      altFormat: 'd/m/Y',
      allowInput: true,
    };
  }

  convertToFlatpickrDate(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.error('Invalid date format:', dateString);
      return '';
    }

    return [date.getDate().toString().padStart(2, '0'), (date.getMonth() + 1).toString().padStart(2, '0'), date.getFullYear()].join('/');
  }

  async saveData(): Promise<void> {
    console.log('Form value before saving:', this.form.value);

    // Ensure expired_date is correctly formatted as a string
    let expiredDate = this.form.value.expired_date;

    if (Array.isArray(expiredDate) && expiredDate.length > 0) {
      expiredDate = expiredDate[0]; // Take the first element if it's an array
    }

    if (expiredDate instanceof Date) {
      expiredDate = this.formatDate(expiredDate);
    }

    this.form.patchValue({ expired_date: expiredDate });

    console.log('Form value after date formatting:', this.form.value);

    if (this.id) {
      await this.updateMerchant();
      return;
    }
    await this.createMerchant();
  }

  async createMerchant(): Promise<void> {
    this.loading = true;
    try {
      const resp = await firstValueFrom(this.merchantService.createMerchant(this.form.value));

      if (resp.code === 0) {
        this.showNotification('success', 'Success', 'Merchant created successfully');
        await this.router.navigate(['/merchants/list']);
      } else {
        this.showNotification('error', 'Error', 'Failed to create merchant');
      }
    } catch (error) {
      this.showNotification('error', 'Error', JSON.stringify(error));
    } finally {
      this.loading = false;
    }
  }

  async updateMerchant(): Promise<void> {
    this.loading = true;
    try {
      const resp = await firstValueFrom(this.merchantService.updateMerchant(this.id, this.form.value));

      if (resp.code === 0) {
        this.showNotification('success', 'Success', 'Merchant updated successfully');
        await this.router.navigate(['/merchants/list']);
      } else {
        this.showNotification('error', 'Error', 'Failed to update merchant');
      }
    } catch (error: any) {
      console.log('Error:', error);
      this.showNotification('error', 'Error', error);
    } finally {
      this.loading = false;
    }
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0]; // Format as 'YYYY-MM-DD'
  }

  private showNotification(type: 'success' | 'error', title: string, text = 'Success'): void {
    showAlert({ type, title, text }).then();
  }

  protected readonly MasterDepositFormValidations = MasterDepositFormValidations;
}
